// Configuration detectors — Detection Engine System 1 (spec §3.1).
//
// Formalized from prior-art `scratch_bio_reformat/sweep_sectionF.py` +
// `remark_s201.py`. Single-layer thresholds are VERBATIM from Section B (s173,
// frozen — НЕ трогать). Engine-mandated fixes over the prototype:
//   • field-level-recovery: stage-alignment (sweep под-реализует, spec §3.1 MED4)
//   • triplet-as-one + group-vs-group (отсутствуют в прототипе, нужны §2.1)
//   • touched-aware others-mean (active-layer denominator, §1)
//
// Каждый детектор возвращает объекты detection:
//   { family, type, layers:[...], strength:number, ...detail }
// family ∈ {field, singleton, opposition, together}.
// type без места в Tier 1–3 (parallel-partial / pair-counterphase) помечается
// family:'untiered' → carrier проваливается в силуэт (catch-all §2.1).

import { layerIndex } from './layers.js';
import {
  seriesTouched,
  othersMeanAt,
  gapAt,
  mean,
  isTouchedAt,
  touchedStagesOf,
} from './input.js';
import { THRESHOLDS as T } from './thresholds.js';
import { interiorExtrema } from './features.js';

/**
 * Отвеченные точки L (нетронутый-на-этапе слайдер выброшен, НЕ читается как 0) — на
 * них считаются формо-моды §2.3 (v-shape / cycling). На all-touched === series(L).
 */
const answered = (stages, L) => seriesTouched(stages, L).filter((x) => x != null);

// ── single-layer primitives (verbatim Section B) ───────────────────────────

/** v-shape: |end−start|≤1, провал ≥ 2 ниже min(start,end). */
export function isVShape(v) {
  if (v.length < 3) return false;
  const start = v[0];
  const end = v[v.length - 1];
  if (Math.abs(end - start) > 1) return false;
  const mid = v.slice(1, -1);
  return Math.min(...mid) <= Math.min(start, end) - T.VSHAPE_DROP;
}

/** Индекс самого глубокого внутреннего провала (для stage-alignment). */
export function troughIndex(v) {
  let idx = 1;
  for (let i = 1; i < v.length - 1; i++) if (v[i] < v[idx]) idx = i;
  return idx;
}

/** mean-crossings (смены знака относительно среднего). */
export function meanCrossings(v) {
  const m = mean(v);
  const signs = v
    .map((x) => (x > m ? 1 : x < m ? -1 : 0))
    .filter((s) => s !== 0);
  let c = 0;
  for (let i = 1; i < signs.length; i++) if (signs[i] !== signs[i - 1]) c++;
  return c;
}

/** cycling: ≥2 внутр. экстремума ∧ ≥3 mean-crossings ∧ амплитуда ≥ 2. */
export function isCycling(v) {
  const { extremaMax, extremaMin } = interiorExtrema(v);
  const ext = extremaMax.length + extremaMin.length;
  const amp = Math.max(...v) - Math.min(...v);
  return ext >= 2 && meanCrossings(v) >= 3 && amp >= T.CYCLING_MIN_AMP;
}

// ── singleton modes (gap = L − mean(other active)) ──────────────────────────

// Singleton gap-modes are per-stage touched-aware (s443.d5 Фаза 2): a stage where L
// itself is untouched carries a default 0, NOT an answered position — reading its gap
// there would fabricate a singleton the user never entered. So each mode iterates over
// `touchedStagesOf(L)` and skips untouched-at-stage. On all-touched this is [0..N−1] →
// identical behavior (byte-parity). "Current" (NOW = last stage) is unanswerable when L
// is untouched there → no current/last-collapse reading.

export function currentSingleton(stages, active, L) {
  const idx = stages.length - 1;
  if (!isTouchedAt(stages[idx], L)) return null; // L unanswered at NOW → no current reading
  const gap = gapAt(stages, active, L, idx);
  if (Math.abs(gap) >= T.SINGLETON_GAP)
    return { stage: idx, dir: gap > 0 ? 'above' : 'below', gap };
  return null;
}

export function historicalSingleton(stages, active, L) {
  const out = [];
  const last = stages.length - 1;
  for (const i of touchedStagesOf(stages, L)) {
    if (i === last) continue;
    const gap = gapAt(stages, active, L, i);
    if (Math.abs(gap) >= T.SINGLETON_GAP)
      out.push({ stage: i, dir: gap > 0 ? 'above' : 'below', gap });
  }
  return out;
}

/** drifting: |gap| растёт через тронутые этапы (collapses ≤ 1 ∧ gap_last−gap_first ≥ 2). */
export function isDrifting(stages, active, L) {
  const ts = touchedStagesOf(stages, L);
  if (ts.length < 2) return false;
  const gaps = ts.map((i) => Math.abs(gapAt(stages, active, L, i)));
  let collapses = 0;
  for (let i = 1; i < gaps.length; i++) if (gaps[i] < gaps[i - 1]) collapses++;
  const rising = gaps[gaps.length - 1] - gaps[0] >= T.DRIFT_RISE;
  return collapses <= 1 && rising;
}

/** last-to-collapse: K≥2 тронутых этапов gap≥+2, затем L(N) ≤ mean(other)+1. */
export function isLastToCollapse(stages, active, L) {
  const idx = stages.length - 1;
  if (!isTouchedAt(stages[idx], L)) return false; // final position unanswered
  let held = 0;
  for (const i of touchedStagesOf(stages, L)) {
    if (i === idx) continue;
    if (gapAt(stages, active, L, i) >= T.SINGLETON_GAP) held++;
  }
  const endOk =
    stages[idx].values[L] <=
    othersMeanAt(stages, active, L, idx) + T.COLLAPSE_END_TOL;
  return held >= T.COLLAPSE_HOLD_MIN && endOk;
}

/** first-to-collapse: K≥2 тронутых этапов gap≤−2, позже остальные тоже падают. */
export function isFirstToCollapse(stages, active, L) {
  const idx = stages.length - 1;
  let early = 0;
  for (const i of touchedStagesOf(stages, L)) {
    if (i === idx) continue;
    if (gapAt(stages, active, L, i) <= -T.SINGLETON_GAP) early++;
  }
  const othersFell =
    othersMeanAt(stages, active, L, idx) < othersMeanAt(stages, active, L, 0);
  return early >= T.COLLAPSE_HOLD_MIN && othersFell;
}

// ── pair geometries (touched-aware) ─────────────────────────────────────────

const pairGap = (stages, a, b, i) => Math.abs(stages[i].values[a] - stages[i].values[b]);

// Sceptic search is per-stage touched-aware (s382): a NON-touched slider (default
// 0) must NOT count as a set 0 — else an all-untouched stage draws 7 equal lines
// and fabricates a parallel/convergence that the user never entered. Returns the
// stage indices where BOTH layers are touched. `isTouchedAt` (input.js) is robust to
// an absent `touched` mask (raw fixtures), so all-touched inputs behave as before.
const touchedBoth = (stages, a, b) =>
  stages.map((_, i) => i).filter((i) => isTouchedAt(stages[i], a) && isTouchedAt(stages[i], b));

/**
 * Сила pairwise-оппозиции = двусторонний раскол ПРОТИВ поля (spec §3.3.1), НЕ
 * gap до дальней линии. Поле = активные слои минус сама пара. Оппозиция сильна
 * настолько, насколько движется её слабейшая сторона: лишь когда ОБА слоя
 * заметно отклонены от поля в РАЗНЫЕ стороны, это «два лагеря». Линия, ушедшая
 * от цельного поля (партнёр ≈ на поле), набирает near-zero силу → singleton
 * честно выигрывает §3.3 тай-брейк. Заменяет прежний `max pairGap`.
 */
export function oppositionStrengthAt(stages, active, a, b, i) {
  const rest = active.filter((L) => L !== a && L !== b);
  const fieldMean = rest.length
    ? mean(rest.map((L) => stages[i].values[L]))
    : (stages[i].values[a] + stages[i].values[b]) / 2;
  const dA = stages[i].values[a] - fieldMean;
  const dB = stages[i].values[b] - fieldMean;
  // «На поле» через noise-floor, не через exact !== 0 (s443.d5 Ф2): float-арифметика
  // среднего даёт dust/−0, а не ровный ноль. EPS_NOISE не двигает содержательный
  // порог — на целых слайдерах поведение идентично прежнему exact-0 (byte-parity).
  const offField = (d) => Math.abs(d) > T.EPS_NOISE;
  if (offField(dA) && offField(dB) && Math.sign(dA) !== Math.sign(dB))
    return 2 * Math.min(Math.abs(dA), Math.abs(dB)); // два лагеря по разные стороны поля
  return Math.abs(Math.abs(dA) - Math.abs(dB)); // одна сторона ≈ на поле → остаточная сила
}

export function isParallelWhole(stages, a, b) {
  const idx = touchedBoth(stages, a, b);
  return idx.length >= 2 && idx.every((i) => pairGap(stages, a, b, i) <= T.PARALLEL_MAX_GAP);
}

/** parallel-partial: параллельны на K подряд (с начала), затем gap ≥ 2; 3 ≤ K < N. */
export function parallelPartialK(stages, a, b) {
  const idx = touchedBoth(stages, a, b);
  if (idx.length < 3) return 0;
  let k = 0;
  while (k < idx.length && pairGap(stages, a, b, idx[k]) <= T.PARALLEL_MAX_GAP) k++;
  if (k >= 3 && k < idx.length && pairGap(stages, a, b, idx[k]) >= T.DIVERGENCE_MIN_GAP)
    return k;
  return 0;
}

export function isDivergence(stages, a, b) {
  const idx = touchedBoth(stages, a, b);
  if (idx.length < 2) return false;
  return (
    pairGap(stages, a, b, idx[0]) <= T.PARALLEL_MAX_GAP &&
    pairGap(stages, a, b, idx[idx.length - 1]) >= T.DIVERGENCE_MIN_GAP
  );
}

export function isLateConvergence(stages, a, b) {
  const idx = touchedBoth(stages, a, b);
  if (idx.length < 2) return false;
  return (
    pairGap(stages, a, b, idx[0]) >= T.DIVERGENCE_MIN_GAP &&
    pairGap(stages, a, b, idx[idx.length - 1]) <= T.PARALLEL_MAX_GAP
  );
}

export function isAntiCorrelation(stages, a, b) {
  const idx = touchedBoth(stages, a, b);
  if (idx.length <= 1) return false;
  let inv = 0;
  for (let j = 1; j < idx.length; j++) {
    const i = idx[j], p = idx[j - 1];
    const da = stages[i].values[a] - stages[p].values[a];
    const db = stages[i].values[b] - stages[p].values[b];
    if ((da >= 1 && db <= -1) || (da <= -1 && db >= 1)) inv++;
  }
  return inv >= idx.length - 1;
}

// ── field-level-recovery (stage-aligned — engine fix over prototype) ────────

/**
 * field-level-recovery (A.4.2 / s203): v-shape на ≥4 слоях с общим этапом
 * провала. Spec §3.1 строже прототипа: требуется stage-alignment, иначе 4
 * несинхронных V ложно схлопнутся. Возврат — на общем этапе (следует из v-shape
 * на каждом слое после общего trough).
 */
export function detectFieldLevel(stages, active) {
  const vLayers = active.filter((L) => isVShape(answered(stages, L)));
  if (vLayers.length < T.FIELD_LEVEL_MIN_LAYERS) return null;

  // Группируем v-shape слои по общему этапу провала.
  const byTrough = new Map();
  for (const L of vLayers) {
    const t = troughIndex(answered(stages, L));
    if (!byTrough.has(t)) byTrough.set(t, []);
    byTrough.get(t).push(L);
  }
  let bestTrough = -1;
  let bestLayers = [];
  for (const [t, ls] of byTrough)
    if (ls.length > bestLayers.length) {
      bestLayers = ls;
      bestTrough = t;
    }
  if (bestLayers.length < T.FIELD_LEVEL_MIN_LAYERS) return null;

  // Красная линия = средняя V поля → её амплитуда = сила.
  const meanCurve = stages.map((_, i) =>
    mean(bestLayers.map((L) => stages[i].values[L])),
  );
  return {
    family: 'field',
    type: 'field-level-recovery',
    layers: bestLayers.slice(),
    trough: bestTrough,
    strength: Math.max(...meanCurve) - Math.min(...meanCurve),
  };
}

// ── together groups: triplet-as-one ─────────────────────────────────────────

/** Все слои группы попарно parallel-whole (взаимно |diff|≤1 ∀ stage). */
function groupMutuallyParallel(stages, group) {
  for (let i = 0; i < group.length; i++)
    for (let j = i + 1; j < group.length; j++)
      if (!isParallelWhole(stages, group[i], group[j])) return false;
  return true;
}

/** Максимальные взаимно-параллельные группы размера ≥3 (triplet-as-one A.3.1). */
export function detectTriplets(stages, active) {
  const out = [];
  // greedy maximal cliques по отношению parallel-whole (граф мал — 7 узлов).
  const seen = new Set();
  for (let i = 0; i < active.length; i++) {
    const group = [active[i]];
    for (let j = 0; j < active.length; j++) {
      if (j === i) continue;
      if (group.every((g) => isParallelWhole(stages, g, active[j])))
        group.push(active[j]);
    }
    if (group.length >= 3 && groupMutuallyParallel(stages, group)) {
      const key = group.slice().sort().join('|');
      if (!seen.has(key)) {
        seen.add(key);
        const meanCurve = stages.map((_, s) =>
          mean(group.map((L) => stages[s].values[L])),
        );
        out.push({
          family: 'together',
          type: 'triplet-as-one',
          layers: group.slice().sort((a, b) => layerIndex(a) - layerIndex(b)),
          strength: Math.max(...meanCurve) - Math.min(...meanCurve),
        });
      }
    }
  }
  return out;
}

// ── group-vs-group (A.3.2) ──────────────────────────────────────────────────

/**
 * group-vs-group: два непустых блока взаимно-параллельных слоёв, расходящихся
 * между собой (gap средних кривых: старт ≤1, финал ≥2). Консервативно: блоки —
 * непересекающиеся triplet/pair кластеры. Возвращает доминантный/др. блок.
 */
export function detectGroupVsGroup(stages, active) {
  // Кандидаты-блоки: взаимно-параллельные кластеры размера ≥2.
  const blocks = [];
  for (let i = 0; i < active.length; i++) {
    const group = [active[i]];
    for (let j = 0; j < active.length; j++) {
      if (j === i) continue;
      if (group.every((g) => isParallelWhole(stages, g, active[j])))
        group.push(active[j]);
    }
    if (group.length >= 2) {
      const key = group.slice().sort().join('|');
      if (!blocks.some((b) => b.key === key))
        blocks.push({ key, layers: group.slice() });
    }
  }
  // Пара непересекающихся блоков, расходящихся по средним кривым.
  let best = null;
  for (let i = 0; i < blocks.length; i++)
    for (let j = i + 1; j < blocks.length; j++) {
      const A = blocks[i].layers;
      const B = blocks[j].layers;
      if (A.some((l) => B.includes(l))) continue;
      const curveA = stages.map((_, s) => mean(A.map((L) => stages[s].values[L])));
      const curveB = stages.map((_, s) => mean(B.map((L) => stages[s].values[L])));
      const startGap = Math.abs(curveA[0] - curveB[0]);
      const endGap = Math.abs(curveA[curveA.length - 1] - curveB[curveB.length - 1]);
      if (startGap <= T.PARALLEL_MAX_GAP && endGap >= T.DIVERGENCE_MIN_GAP) {
        const ampA = Math.max(...curveA) - Math.min(...curveA);
        const ampB = Math.max(...curveB) - Math.min(...curveB);
        const strength = endGap;
        if (!best || strength > best.strength)
          best = {
            family: 'opposition',
            type: 'group-vs-group',
            layers: [...A, ...B],
            blocks: [A.slice(), B.slice()],
            dominantBlock: ampA >= ampB ? A.slice() : B.slice(),
            strength,
          };
      }
    }
  return best;
}

// ── top-level aggregation ───────────────────────────────────────────────────

/**
 * Все сработавшие конфигурации над активными слоями.
 * Возвращает плоский список detection-объектов (до mutex/selection).
 */
export function detectAll(stages, active) {
  const detections = [];
  const N = stages.length;

  // field-level (page-level) — отдельно.
  const field = detectFieldLevel(stages, active);
  if (field) detections.push(field);

  // single-layer modes.
  for (const L of active) {
    const v = answered(stages, L); // shape reads on answered points (§2.3, s443.d5 Ф2)

    const cs = currentSingleton(stages, active, L);
    if (cs)
      detections.push({
        family: 'singleton',
        type: 'current-singleton',
        layers: [L],
        dir: cs.dir,
        stage: cs.stage,
        strength: Math.abs(cs.gap),
      });

    const hs = historicalSingleton(stages, active, L);
    if (hs.length) {
      // dir — единый контракт позиционных одиночек (s329): знак с этапа макс.
      // отрыва (того, что задаёт strength).
      const domHs = hs.reduce((a, b) => (Math.abs(b.gap) > Math.abs(a.gap) ? b : a));
      detections.push({
        family: 'singleton',
        type: 'historical-singleton',
        layers: [L],
        dir: domHs.dir,
        stages: hs.map((h) => h.stage),
        strength: Math.max(...hs.map((h) => Math.abs(h.gap))),
      });
    }

    if (isDrifting(stages, active, L)) {
      const gaps = stages.map((_, i) => Math.abs(gapAt(stages, active, L, i)));
      // dir — знак gap на финале (там дрейф максимален), s329.
      const finalGap = gapAt(stages, active, L, stages.length - 1);
      detections.push({
        family: 'singleton',
        type: 'drifting-singleton',
        layers: [L],
        dir: finalGap > 0 ? 'above' : 'below',
        strength: Math.max(...gaps),
      });
    }

    if (isLastToCollapse(stages, active, L))
      detections.push({
        family: 'singleton',
        type: 'last-to-collapse',
        layers: [L],
        strength: Math.max(
          ...stages.slice(0, -1).map((_, i) => gapAt(stages, active, L, i)),
        ),
      });

    if (isFirstToCollapse(stages, active, L))
      detections.push({
        family: 'singleton',
        type: 'first-to-collapse',
        layers: [L],
        strength: Math.max(
          ...stages.slice(0, -1).map((_, i) => Math.abs(gapAt(stages, active, L, i))),
        ),
      });

    if (isVShape(v)) {
      // §3.2 guard = current gap at L's LAST ANSWERED stage (untouched NOW ⇒ its 0 is
      // not a reading). On all-touched this is stage N−1 → identical.
      const ts = touchedStagesOf(stages, L);
      const idx = ts.length ? ts[ts.length - 1] : N - 1;
      const guard = Math.abs(gapAt(stages, active, L, idx));
      detections.push({
        family: 'singleton',
        type: 'v-shape-recovery',
        layers: [L],
        trough: troughIndex(v),
        guard,
        strength: Math.max(...v) - Math.min(...v),
      });
    }

    if (isCycling(v))
      detections.push({
        family: 'singleton',
        type: 'singleton-cycling',
        layers: [L],
        strength: Math.max(...v) - Math.min(...v),
      });
  }

  // pair geometries (touched pairs).
  for (let i = 0; i < active.length; i++)
    for (let j = i + 1; j < active.length; j++) {
      const a = active[i];
      const b = active[j];
      if (isParallelWhole(stages, a, b)) {
        const curve = stages.map((_, s) => (stages[s].values[a] + stages[s].values[b]) / 2);
        detections.push({
          family: 'together',
          type: 'parallel-whole',
          layers: [a, b],
          strength: Math.max(...curve) - Math.min(...curve),
        });
      }
      if (isDivergence(stages, a, b))
        detections.push({
          family: 'opposition',
          type: 'divergence-over-time',
          layers: [a, b],
          // раскол на финале (§3.3.1) — против поля, не до дальней линии.
          strength: oppositionStrengthAt(stages, active, a, b, N - 1),
        });
      if (isLateConvergence(stages, a, b))
        detections.push({
          family: 'opposition',
          type: 'late-convergence',
          layers: [a, b],
          // раскол на старте (§3.3.1) — пара далеко в начале, сходится к финалу.
          strength: oppositionStrengthAt(stages, active, a, b, 0),
        });
      if (isAntiCorrelation(stages, a, b))
        detections.push({
          family: 'opposition',
          type: 'anti-correlation',
          layers: [a, b],
          // макс двустороннего раскола по этапам (§3.3.1).
          strength: Math.max(
            ...stages.map((_, s) => oppositionStrengthAt(stages, active, a, b, s)),
          ),
        });
      const ppK = parallelPartialK(stages, a, b);
      if (ppK)
        detections.push({
          family: 'untiered',
          type: 'parallel-partial',
          layers: [a, b],
          k: ppK,
          strength: 0,
        });
    }

  // together-groups + group-vs-group.
  detections.push(...detectTriplets(stages, active));
  const gvg = detectGroupVsGroup(stages, active);
  if (gvg) detections.push(gvg);

  return detections;
}
